import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarIcon,
  ArrowLeft,
  Plus,
  X,
  Trash2,
  Building,
  DollarSign,
  ChevronsUpDown,
  FileCheck,
  Scale,
  Shield,
  Briefcase,
  Users,
  Globe,
  Gavel,
  Leaf,
  ArrowRight,
  CheckCircle,
  Loader2,
  UserCog,
  Eye,
  Search,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { dealService } from "@/services/dealService";
import { DealType, DEAL_TYPE_LABELS, PersonaType } from "@/types/deal";
import { CurrencyValueInput } from "@/components/ui/currency-value-input";
import { getPreviousStepNavigation } from "../utils/stepNavigation";
import { personaService, PersonaDefinition } from "@/services/personaService";
import { Checkbox } from "@/components/ui/checkbox";
import { Brain } from "lucide-react";
import { getDealFieldConfig } from "@/config/dealFieldConfig";
import { DynamicDealFields } from "@/components/ui/dynamic-deal-fields";
import countryList from 'country-list';

// NEW: services for step 4
import {
  dealPersonaService,
  type DealPersonaRole,
} from "@/services/dealPersonaService";
import { dealViewerService } from "@/services/dealViewerService";
import { userService } from "@/services/userService";
import type { User } from "@/types/auth";
import { requisitionService } from "@/services/requisitionService";
import type { RequisitionTemplate } from "@/types/requisition";
import documentService from "@/services/documentService";
import { aiReviewService } from "@/services/aiReviewService";

// NEW: async, role-aware pickers
import { AsyncUsersMultiSelect } from "@/components/AsyncUsersMultiSelect";
import { AsyncUserSelect } from "@/components/AsyncUserSelect";

// Icon mapping for categories
const CATEGORY_ICON_MAP: { [key: string]: any } = {
  "Corporate and Secretarial": Building,
  "Financial General Aspects": DollarSign,
  Contracts: FileCheck,
  Liabilities: Scale,
  "Regulatory/Legal Approvals and Permits": Shield,
  Assets: Briefcase,
  "Industrial and Intellectual Property": Globe,
  "Labour and Social Security Matter": Users,
  Litigation: Gavel,
  Environment: Leaf,
};
// Get sorted country list
const COUNTRIES = countryList.getData().sort((a, b) => a.name.localeCompare(b.name));
// Initial requisition categories
const initialRequisitionCategories = [
  {
    id: "corporate",
    title: "Corporate and Secretarial",
    icon: Building,
    items: [
      "Organizational structure of the Company, covering all its subsidiaries and affiliates",
      "Articles and Memorandum of Association of the Company, along with Certificate of Incorporation",
      "Minutes book and minutes of meetings of Board of directors, Committees of directors, and Shareholders",
      "Copies of all registers maintained by the Company",
      "Details of share capital amount and shareholding structure",
      "Details and documents pertaining to any convertible securities issued by the Company",
      "List of agreements in force, executed between the Company and its shareholders or third parties",
      "Any obligations relating to the sale, purchase, assignment, transfer, issuance, acquisition or encumbrance of shares",
      "Details of auditors for the last three financial years",
    ],
  },
  {
    id: "financial",
    title: "Financial General Aspects",
    icon: DollarSign,
    items: [
      "Audited Financial Statements for the last three years and latest YTD financial information",
      "Particulars and list of bank accounts in any currency and jurisdiction",
      "Reports of board of directors, Company's auditors, unaudited financials",
    ],
  },
  {
    id: "contracts",
    title: "Contracts",
    icon: FileCheck,
    items: [
      "Copies of all commercial contracts for development projects",
      "Copies of utilities agreements (power, water, etc.)",
      "Details and copies of any other contracts/agreements relating to the business",
      "Copy of all agreements involving exclusivity, reciprocity or non-competition arrangements",
    ],
  },
  {
    id: "liabilities",
    title: "Liabilities",
    icon: Scale,
    items: [
      "List showing structure of financial liabilities and all financing documents",
      "Details of guarantees, bank/performance guarantees, standby letters of credit",
      "Details of contingent liabilities or off-balance-sheet obligations",
    ],
  },
  {
    id: "regulatory",
    title: "Regulatory/Legal Approvals and Permits",
    icon: Shield,
    items: [
      "Detailed list and copies of all licenses, permits, registrations, concessions, approvals",
      "Details of conditions for maintaining licenses, permits, authorizations, and consents",
      "Copies of all relevant tax registrations, capital subsidy schemes, licenses",
    ],
  },
  {
    id: "assets",
    title: "Assets",
    icon: Briefcase,
    items: [
      "Details and documents pertaining to immovable properties owned/taken on lease",
      "Details and documents pertaining to movable properties including charges and encumbrances",
    ],
  },
  {
    id: "ip",
    title: "Industrial and Intellectual Property",
    icon: Globe,
    items: [
      "Details of trademarks, patents, copyrights, designs and models or other intellectual property rights",
    ],
  },
  {
    id: "labour",
    title: "Labour and Social Security Matter",
    icon: Users,
    items: [
      "Detailed list of employees (permanent and contractual)",
      "Copies of standard employment contracts and service agreements",
      "List of insurances taken by Company for Employees",
    ],
  },
  {
    id: "litigation",
    title: "Litigation",
    icon: Gavel,
    items: [
      "List of Disputes before any court, tribunal, quasi judicial or administrative body",
      "List of all resolved Disputes involving the Company or affiliates",
      "Details of any current or threatened tax Disputes with relevant tax authority",
    ],
  },
  {
    id: "environment",
    title: "Environment",
    icon: Leaf,
    items: [
      "List and copies of all licenses, authorizations, permits from environmental agencies",
      "Details of waste/effluents/hazardous substances management and disposal systems",
    ],
  },
];

const RegisterDeal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Track validation attempt state
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [jurisdictionOpen, setJurisdictionOpen] = useState(false);
  const [supplierJurisdictionOpen, setSupplierJurisdictionOpen] = useState<{[key: number]: boolean}>({});

  // Get dealId from query params
  const searchParams = new URLSearchParams(location.search);
  const dealId = searchParams.get("dealId");

  // Get navigation info for previous step
  const previousStepNav = getPreviousStepNavigation("register-deal");

  // Get organization's default currency
  const orgDefaultCurrency = user?.organization?.defaultCurrency || 'INR';

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    type: "" as DealType | "",
    currency: orgDefaultCurrency,
    value: "",
    jurisdiction: "",
    buyer: "",
    seller: "",
    startDate: "",
    expectedSigningDate: "",
    expectedClosingDate: "",
    notes: "",
    industry: "",
    description: "",
    riskLevel: "MEDIUM" as "LOW" | "MEDIUM" | "HIGH",
    confidentialityLevel: "INTERNAL" as
      | "PUBLIC"
      | "INTERNAL"
      | "CONFIDENTIAL"
      | "RESTRICTED",
    // M&A specific fields
    dealStructure: "",
    paymentType: "",
    synergiesExpected: "",
    // Debt Financing specific fields
    debtType: "",
    interestRate: "",
    maturityDate: "",
    covenants: "",
    // Investment specific fields
    fundingRound: "",
    equityStake: "",
    preMoneyValuation: "",
    postMoneyValuation: "",
    boardRights: "",
  });

  // Suppliers
  const [suppliers, setSuppliers] = useState<
    Array<{
      name: string;
      jurisdiction: string;
      contactInfo: string;
      description: string;
    }>
  >([{ name: "", jurisdiction: "", contactInfo: "", description: "" }]);

  const [signingDate, setSigningDate] = useState<Date>();
  const [closingDate, setClosingDate] = useState<Date>();
  const [documents, setDocuments] = useState<File[]>([]);

  // Personas (step 3)
  const [personas, setPersonas] = useState<PersonaDefinition[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);
  const [loadingPersonas, setLoadingPersonas] = useState(true);
  const [dealOriginalPersonas, setDealOriginalPersonas] = useState<string[]>([]);

  // Requisition list
  const [requisitionCategories, setRequisitionCategories] = useState<any[]>([]);
  const [loadingRequisitions, setLoadingRequisitions] = useState(true);
  const [requisitionTemplate, setRequisitionTemplate] =
    useState<RequisitionTemplate | null>(null);
  const [newItemInputs, setNewItemInputs] = useState<{
    [categoryId: string]: string;
  }>({});
  const [showNewItemInputs, setShowNewItemInputs] = useState<{
    [categoryId: string]: boolean;
  }>({});

  // NEW — analysts (Step 1 assignment)
  const [analystIds, setAnalystIds] = useState<string[]>([]);

  // NEW — Step 4: assignment state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [personaAssignments, setPersonaAssignments] = useState<
    Record<string, { userId: string | null; role: DealPersonaRole }>
  >({});
  const [viewerUserIds, setViewerUserIds] = useState<string[]>([]);

  const [existingPersonaMap, setExistingPersonaMap] = useState<Record<string, string | null>>({});
  const [existingViewerIds, setExistingViewerIds] = useState<string[]>([]);

  // Confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    categoryId: string;
    itemIndex: number;
    itemName: string;
    hasDocuments: boolean;
  } | null>(null);

  // Fetch users once (needed for Step 1 + Step 4 fallback labels)
  useEffect(() => {
    (async () => {
      try {
        const users = await userService.listAll();
        setAllUsers(users);
      } catch (e) {
        // non-fatal; async pickers use server search
      }
    })();
  }, []);

  // Load default requisition template
  useEffect(() => {
    const loadRequisitionTemplate = async () => {
      try {
        setLoadingRequisitions(true);
        const template = await requisitionService.getDefaultGeneralTemplate();
        setRequisitionTemplate(template);

        // Convert template to the format expected by the frontend
        const categories = template.categories.map((category) => ({
          id: category.id,
          title: category.name,
          icon: CATEGORY_ICON_MAP[category.name] || Building,
          items: category.items.map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            isRequired: item.isRequired,
            acceptedFormats: item.acceptedFormats || [],
            maxFileSize: item.maxFileSize,
            examples: item.examples || [],
          })),
        }));
        setRequisitionCategories(categories);
      } catch (error) {
        console.error("Failed to load requisition template:", error);
        toast({
          title: "Failed to load requisition template",
          description: "Using default template. Please try refreshing the page.",
          variant: "destructive",
        });
        // Fallback to empty array or could use initialRequisitionCategories
        setRequisitionCategories([]);
      } finally {
        setLoadingRequisitions(false);
      }
    };

    loadRequisitionTemplate();
  }, [toast]);

  // If editing, fetch deal data and populate form
  useEffect(() => {
    if (dealId) {
      setLoading(true);
      dealService
        .getDealById(dealId)
        .then((deal) => {
          setFormData({
            title: deal.title || "",
            type: deal.type || "",
            currency: deal.currency || orgDefaultCurrency,
            value: deal.value?.toString() || "",
            jurisdiction: deal.jurisdiction || "",
            buyer: deal.buyer || "",
            seller: deal.seller || "",
            startDate: deal.startDate || "",
            expectedSigningDate: deal.expectedSigningDate || "",
            expectedClosingDate: deal.expectedClosingDate || "",
            notes: Array.isArray(deal.notes)
              ? (deal.notes as any[]).map((n: any) => n.note).join("\n")
              : deal.notes || "",
            industry: deal.industry || "",
            description: deal.description || "",
            riskLevel: deal.riskLevel || "MEDIUM",
            confidentialityLevel: deal.confidentialityLevel || "INTERNAL",
            dealStructure: (deal as any).dealStructure || "",
            paymentType: (deal as any).paymentType || "",
            synergiesExpected: (deal as any).synergiesExpected || "",
            debtType: (deal as any).debtType || "",
            interestRate: (deal as any).interestRate?.toString() || "",
            maturityDate: (deal as any).maturityDate || "",
            covenants: (deal as any).covenants || "",
            fundingRound: (deal as any).fundingRound || "",
            equityStake: (deal as any).equityStake?.toString() || "",
            preMoneyValuation:
              (deal as any).preMoneyValuation?.toString() || "",
            postMoneyValuation:
              (deal as any).postMoneyValuation?.toString() || "",
            boardRights: (deal as any).boardRights || "",
          });

          if ((deal as any).analysts && Array.isArray((deal as any).analysts)) {
            setAnalystIds((deal as any).analysts.map((a: any) => a.userId));
          } else if (
            (deal as any).analystIds &&
            Array.isArray((deal as any).analystIds)
          ) {
            setAnalystIds((deal as any).analystIds);
          }

          if (deal.suppliers) {
            setSuppliers(
              deal.suppliers.map((supplier: any) => ({
                ...supplier,
                contactInfo: supplier.contactInfo?.email || "",
              }))
            );
          }
          if (deal.expectedSigningDate)
            setSigningDate(new Date(deal.expectedSigningDate));
          if (deal.expectedClosingDate)
            setClosingDate(new Date(deal.expectedClosingDate));

          if (deal.selectedPersonas) {
            setDealOriginalPersonas(deal.selectedPersonas);
          }

          if (
            deal.customRequisitionData &&
            Array.isArray(deal.customRequisitionData.categories)
          ) {
            setRequisitionCategories(
              deal.customRequisitionData.categories.map((cat: any) => ({
                ...cat,
                icon:
                  CATEGORY_ICON_MAP[cat.title] ||
                  CATEGORY_ICON_MAP[cat.name] ||
                  Building,
              }))
            );
          }
        })
        .catch(() => {
          toast({
            title: "Error",
            description: "Failed to load deal for editing.",
            variant: "destructive",
          });
        })
        .finally(() => setLoading(false));
    }
  }, [dealId, toast]);

  useEffect(() => {
  if (currentStep !== 4 || !dealId) return;
  (async () => {
    try {
      const personas = await dealPersonaService.listAssignees(dealId);
      const map: Record<string, string | null> = {};
      personas.forEach(p => { map[p.personaType] = p.user?.id ?? null; });
      setExistingPersonaMap(map);

      const viewers = await dealViewerService.list(dealId);
      setExistingViewerIds(viewers.map(v => v.id));
    } catch {}
  })();
}, [currentStep, dealId]);

  // Load personas and user's default selected personas
  useEffect(() => {
    const loadPersonaData = async () => {
      try {
        setLoadingPersonas(true);
        const userSelectedPersonaDefinitions =
          await personaService.getUserSelectedPersonaDefinitions();

        if (
          !userSelectedPersonaDefinitions ||
          userSelectedPersonaDefinitions.length === 0
        ) {
          setPersonas([]);
          setSelectedPersonas([]);
          return;
        }

        setPersonas(userSelectedPersonaDefinitions);

        // pre-select all when creating new deal
        if (!dealId) {
          setSelectedPersonas(userSelectedPersonaDefinitions.map((p) => p.type));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load persona data";
        toast({
          title: "Loading Failed",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoadingPersonas(false);
      }
    };
    loadPersonaData();
  }, [dealId, toast]);

  // If editing, restrict to user's current persona settings
  useEffect(() => {
    if (dealId && personas.length > 0 && dealOriginalPersonas.length > 0) {
      const availablePersonaTypes = personas.map((p) => p.type);
      const filteredSelectedPersonas = dealOriginalPersonas.filter((t) =>
        availablePersonaTypes.includes(t)
      );
      setSelectedPersonas(filteredSelectedPersonas);
    }
  }, [dealId, personas, dealOriginalPersonas]);

  // Step 4 — initialize personaAssignments whenever selection changes
  useEffect(() => {
    if (selectedPersonas?.length) {
      setPersonaAssignments((prev) => {
        const next = { ...prev };
        selectedPersonas.forEach((p) => {
          if (!next[p]) next[p] = { userId: null, role: "REVIEWER" }; // default REVIEWER
        });
        Object.keys(next).forEach((k) => {
          if (!selectedPersonas.includes(k)) delete next[k];
        });
        return next;
      });
    } else {
      setPersonaAssignments({});
    }
  }, [selectedPersonas]);

  // Step 4 — load users when step 4 is active (retry if earlier failed)
  useEffect(() => {
    if (currentStep === 4 && allUsers.length === 0) {
      (async () => {
        try {
          const users = await userService.listAll();
          setAllUsers(users);
        } catch (e) {
          toast({
            title: "Failed to fetch users",
            description:
              "Make sure you're logged in as Admin to assign personas.",
            variant: "destructive",
          });
        }
      })();
    }
  }, [currentStep, allUsers.length, toast]);

  // Handlers
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files)
      setDocuments([...documents, ...Array.from(e.target.files)]);
  };

  const removeDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const addNewItem = (categoryId: string) => {
    const newItemText = newItemInputs[categoryId]?.trim();
    if (!newItemText) return;

    // Generate a temporary ID for custom items
    const customItemId = `custom_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const newItem = {
      id: customItemId,
      name: newItemText,
      description: `Custom requisition item: ${newItemText}`,
      isRequired: false,
      acceptedFormats: ["pdf", "doc", "docx", "xlsx", "xls", "jpg", "png"],
      maxFileSize: 10485760, // 10MB
      examples: [`${newItemText}.pdf`],
    };

    setRequisitionCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? { ...category, items: [...category.items, newItem] }
          : category
      )
    );

    setNewItemInputs((prev) => ({ ...prev, [categoryId]: "" }));
    setShowNewItemInputs((prev) => ({ ...prev, [categoryId]: false }));

    toast({
      title: "Item Added",
      description: "New requisition item has been added successfully.",
    });
  };

  const handleDeleteItemWithConfirmation = async (
    categoryId: string,
    itemIndex: number
  ) => {
    // First, get the item that's being deleted to access its details
    const categoryToUpdate = requisitionCategories.find(
      (cat) => cat.id === categoryId
    );
    if (!categoryToUpdate || !categoryToUpdate.items[itemIndex]) {
      toast({
        title: "Error",
        description: "Item not found.",
        variant: "destructive",
      });
      return;
    }

    const itemToDelete = categoryToUpdate.items[itemIndex];

    // Set the item to delete and open the confirmation dialog
    setItemToDelete({
      categoryId,
      itemIndex,
      itemName: itemToDelete.name,
      hasDocuments: !!dealId, // If editing a deal, assume there might be documents
    });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      await deleteItem(itemToDelete.categoryId, itemToDelete.itemIndex);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const deleteItem = async (categoryId: string, itemIndex: number) => {
    // First, get the item that's being deleted to access its ID
    const categoryToUpdate = requisitionCategories.find(
      (cat) => cat.id === categoryId
    );
    if (!categoryToUpdate || !categoryToUpdate.items[itemIndex]) {
      toast({
        title: "Error",
        description: "Item not found.",
        variant: "destructive",
      });
      return;
    }

    const itemToDelete = categoryToUpdate.items[itemIndex];
    const itemId = itemToDelete.id;

    // If editing an existing deal, delete associated documents
    if (dealId && itemId) {
      try {
        console.log(
          `🔍 Looking for documents associated with item ID: ${itemId} in deal: ${dealId}`
        );

        // Get all documents for this deal
        const documentsResponse = await documentService.getDocuments(dealId);
        console.log(
          `📄 Found ${documentsResponse.documents.length} total documents for deal`
        );

        // Find documents associated with this specific item
        // Use the same logic as DocumentUpload.tsx to find documents with backward compatibility
        const associatedDocuments = documentsResponse.documents.filter(
          (doc) => {
            const matches =
              doc.templateItemId === itemId ||
              doc.requisitionItemId === itemId ||
              doc.templateItemId === `${categoryId}-${itemIndex}` ||
              doc.requisitionItemId === `${categoryId}-${itemIndex}`;

            if (matches) {
              console.log(
                `📎 Found matching document: ${doc.originalName} (ID: ${doc.id}) - matched on ${
                  doc.templateItemId || doc.requisitionItemId
                }`
              );
            }

            return matches;
          }
        );

        // Delete associated documents
        if (associatedDocuments.length > 0) {
          console.log(
            `🗑️ Deleting ${associatedDocuments.length} documents associated with item ${itemId}`
          );

          const deletePromises = associatedDocuments.map((document) =>
            documentService.deleteDocument(document.id).catch((error) => {
              console.error(
                `Failed to delete document ${document.id}:`,
                error
              );
              return error; // Don't fail the entire operation
            })
          );

          await Promise.allSettled(deletePromises);
          
          // After deleting documents, check if total document count becomes 0
          const updatedDocumentsResponse = await documentService.getDocuments(dealId);
          const remainingDocumentCount = updatedDocumentsResponse.documents.length;
          
          console.log(`📊 Remaining documents after deletion: ${remainingDocumentCount}`);
          
          if (remainingDocumentCount === 0) {
            console.log(`🔄 No documents remaining - resetting deal to DRAFT status and clearing AI review`);
            
            try {
              // Reset deal status to DRAFT
              await dealService.updateDealStatus(dealId, {
                status: "DRAFT",
                
              });
              
              // Clear AI review data
              await aiReviewService.resetReview(dealId);
              
              toast({
                title: "Deal Status Reset",
                description: `Requisition item and ${associatedDocuments.length} associated document(s) deleted. Deal status has been reset to DRAFT and AI review data cleared since no documents remain.`,
              });
            } catch (error) {
              console.error('Error resetting deal status or AI review:', error);
              toast({
                title: "Item Deleted with Warning",
                description: `Requisition item and ${associatedDocuments.length} associated document(s) deleted, but there was an issue resetting the deal status or AI review data.`,
                variant: "destructive",
              });
            }
          } else {
            toast({
              title: "Item Deleted",
              description: `Requisition item and ${associatedDocuments.length} associated document(s) deleted successfully.`,
            });
          }
        } else {
          toast({
            title: "Item Deleted",
            description: "Requisition item has been deleted successfully.",
          });
        }
      } catch (error) {
        console.error("Error deleting associated documents:", error);
        // Continue with item deletion even if document deletion fails
        toast({
          title: "Warning",
          description:
            "Item deleted, but there may have been issues deleting associated documents.",
          variant: "destructive",
        });
      }
    } else {
      // Not editing or no itemId, just remove from state
      toast({
        title: "Item Deleted",
        description: "Requisition item has been deleted successfully.",
      });
    }

    // Remove the item from the frontend state
    setRequisitionCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.filter((_, index) => index !== itemIndex),
            }
          : category
      )
    );
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset validation attempted when user makes changes
    if (validationAttempted) {
      setValidationAttempted(false);
    }
  };

  const handleDateFieldChange = (field: string, date: Date | undefined) => {
    if (date) handleInputChange(field, date.toISOString());
    else handleInputChange(field, "");
  };

  const handleSigningDateChange = (date: Date | undefined) => {
    setSigningDate(date);
    if (validationAttempted) {
      setValidationAttempted(false);
    }
  };

  const handleClosingDateChange = (date: Date | undefined) => {
    setClosingDate(date);
    if (validationAttempted) {
      setValidationAttempted(false);
    }
  };

  const navigateToStep = (step: number) => {
    setCurrentStep(step);
    setValidationAttempted(false);
  };

  const handlePersonaToggle = (personaType: string, checked: boolean) => {
    setSelectedPersonas((prev) =>
      checked ? [...prev, personaType] : prev.filter((p) => p !== personaType)
    );
  };

  const handleSupplierChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setSuppliers((prev) =>
      prev.map((supplier, i) =>
        i === index ? { ...supplier, [field]: value } : supplier
      )
    );
  };

  const addSupplier = () => {
    setSuppliers((prev) => [
      ...prev,
      { name: "", jurisdiction: "", contactInfo: "", description: "" },
    ]);
  };

  const removeSupplier = (index: number) => {
    if (suppliers.length > 1) {
      setSuppliers((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Validation step 1
  const validateStep1 = () => {
    const errors: string[] = [];
    
  if (!formData.title.trim()) errors.push("Deal title is required");
  if (!formData.type) errors.push("Deal type is required");

  if (!formData.value || isNaN(Number(formData.value)))
    errors.push("Deal value is required and must be a number");

  if (!formData.jurisdiction.trim())
    errors.push("Jurisdiction is required");

  if (!formData.buyer.trim())
    errors.push("Buyer/Acquirer is required");

  if (!formData.seller.trim())
    errors.push("Seller/Target is required");

  if (!signingDate) errors.push("Term Sheet Date is required");
  if (!closingDate) errors.push("Expected Closing Date is required");

  if (signingDate && closingDate && closingDate <= signingDate) {
    errors.push("Expected Closing Date must be later than Term Sheet Date");
  }

    return errors;
  };

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationAttempted(true);
    const errors = validateStep1();
    if (errors.length > 0) {
      toast({
        title: "Validation Error",
        description: errors.join(", "),
        variant: "destructive",
      });
      return;
    }
    setCurrentStep(2);
  };

  // Build deal payload (shared by step 3 & step 4)
  // NOTE: backend expects **analysts** (not analystIds)
  const buildDealPayload = () => ({
    title: formData.title,
    type: formData.type as DealType,
    currency: formData.currency,
    value: formData.value ? Number(formData.value) : undefined,
    jurisdiction: formData.jurisdiction || undefined,
    buyer: formData.buyer || undefined,
    seller: formData.seller || undefined,
    suppliers: suppliers
      .filter((s) => s.name.trim())
      .map((supplier) => ({
        name: supplier.name.trim(),
        jurisdiction: supplier.jurisdiction.trim(),
        contactInfo: supplier.contactInfo ? { email: supplier.contactInfo } : undefined,
        description: supplier.description || undefined,
      })),
    startDate: formData.startDate || undefined,
    expectedSigningDate: signingDate ? signingDate.toISOString() : undefined,
    expectedClosingDate: closingDate ? closingDate.toISOString() : undefined,
    dates: [
      ...(signingDate
        ? [
            {
              dateType: "SIGNING" as const,
              dateValue: signingDate.toISOString(),
              description: "Expected signing date",
            },
          ]
        : []),
      ...(closingDate
        ? [
            {
              dateType: "CLOSING" as const,
              dateValue: closingDate.toISOString(),
              description: "Expected closing date",
            },
          ]
        : []),
    ],
    notes: formData.notes || undefined,
    industry: formData.industry || undefined,
    description: formData.description || undefined,
    riskLevel: formData.riskLevel,
    confidentialityLevel: formData.confidentialityLevel,
    selectedPersonas: (selectedPersonas as PersonaType[]) || [],
    // M&A specific fields
    dealStructure: formData.dealStructure || undefined,
    paymentType: formData.paymentType || undefined,
    synergiesExpected: formData.synergiesExpected || undefined,
    // Debt Financing specific fields
    debtType: formData.debtType || undefined,
    interestRate: formData.interestRate ? Number(formData.interestRate) : undefined,
    maturityDate: formData.maturityDate || undefined,
    covenants: formData.covenants || undefined,
    // Investment specific fields
    fundingRound: formData.fundingRound || undefined,
    equityStake: formData.equityStake ? Number(formData.equityStake) : undefined,
    preMoneyValuation: formData.preMoneyValuation ? Number(formData.preMoneyValuation) : undefined,
    postMoneyValuation: formData.postMoneyValuation ? Number(formData.postMoneyValuation) : undefined,
    boardRights: formData.boardRights || undefined,
    // custom requisition
    customRequisitionData: {
      categories: requisitionCategories.map((category) => ({
        id: category.id,
        title: category.title,
        items: category.items,
      })),
    },
    // IMPORTANT: backend reads `analysts`
    analysts: analystIds,
  });

  // Original final submit (kept for direct register/update path if needed)
  const handleFinalSubmit = async () => {
    try {
      setLoading(true);
      const dealData = buildDealPayload();

      if (dealId) {
        await dealService.updateDeal(dealId, dealData);
        toast({
          title: "Deal Updated Successfully!",
          description: "Your deal has been updated.",
        });
        navigate(`/deals`);
      } else {
        await dealService.createDeal(dealData);
        toast({
          title: "Deal Registered Successfully!",
          description:
            "Your deal has been registered and is now ready for due diligence.",
        });
        navigate(`/deals`);
      }
    } catch (error) {
      console.error(dealId ? "Failed to update deal:" : "Failed to create deal:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : dealId
            ? "Failed to update deal. Please try again."
            : "Failed to register deal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // NEW — Step 4 combined create/update + assign
 const handleCreateOrUpdateAndAssign = async () => {
  try {
    setLoading(true);
    const payload = buildDealPayload();

    // 1) create or update deal
    const createdOrUpdated = dealId
      ? await dealService.updateDeal(dealId, payload).then(() => ({ id: dealId }))
      : await dealService.createDeal(payload);
    const finalDealId = createdOrUpdated.id;

    // Sync AI Review records with the updated persona list
    if (dealId) { // Only sync for existing deals
      await aiReviewService.syncPersonas(finalDealId, payload.selectedPersonas);
    }

    // 2) personas: send both assignments and explicit unassignments (userId = null)
    //    a) Take all persona keys we know about (existing + currently selected)
    const allPersonaKeys = Array.from(
      new Set([
        ...Object.keys(existingPersonaMap || {}),
        ...Object.keys(personaAssignments || {}),
        ...selectedPersonas, // selected set in step 3
      ])
    );

    const personaPayload = allPersonaKeys.map((personaType) => {
      const current = personaAssignments[personaType]?.userId ?? null;
      // If the persona is not selected anymore, ensure it's removed
      const shouldBeNull = !selectedPersonas.includes(personaType);
      return {
        personaType,
        userId: shouldBeNull ? null : current, // null triggers delete on backend
      };
    });

    // Only send if editing or we actually have any persona keys to apply
    if (personaPayload.length) {
      await dealPersonaService.assign(finalDealId, personaPayload);
    }

    // 3) viewers: add new + remove missing (diff with existingViewerIds)
    if (dealId) {
      const prev = new Set(existingViewerIds);
      const next = new Set(viewerUserIds);

      const toAdd = [...next].filter((id) => !prev.has(id));
      const toRemove = [...prev].filter((id) => !next.has(id));

      if (toAdd.length) {
        await dealViewerService.add(finalDealId, toAdd);
      }
      if (toRemove.length) {
        await Promise.all(toRemove.map((id) => dealViewerService.remove(finalDealId, id)));
      }
    } else {
      // new deal: only add viewers
      if (viewerUserIds.length) {
        await dealViewerService.add(finalDealId, viewerUserIds);
      }
    }

    // 4) analysts: replace list
    await dealService.setAnalysts(finalDealId, analystIds);

    toast({
      title: dealId ? "Deal Updated & Team Assigned" : "Deal Created & Team Assigned",
      description: "Persona assignees, analysts, and viewers have been saved.",
    });
    navigate(`/deals/${finalDealId}`);
  } catch (error: any) {
    console.error(error);
    toast({
      title: "Failed to save",
      description: error?.message ?? "Please try again.",
      variant: "destructive",
    });
  } finally {
    setLoading(false);
  }
};


  // --- RENDERERS ---

  // Step 1 — Analysts section UI (multi-select async picker)
  const renderAnalystSection = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCog className="w-5 h-5" />
          Assign Analysts
        </CardTitle>
        <CardDescription>
          Select one or more <b>ANALYST</b> users. They will manage documents and
          workflow for this deal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <AsyncUsersMultiSelect
  role="ANALYST"
  value={analystIds}
  onChange={(ids) => setAnalystIds(ids)}
  placeholder="Search analysts and select…"
  pageSize={20}
/>

        <p className="text-xs text-muted-foreground">
          You can assign multiple analysts. Use the search to find users across your org.
        </p>
      </CardContent>
    </Card>
  );

  const renderStep1 = () => (
    <form onSubmit={handleStep1Submit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>
            Enter the fundamental details of the deal including main parties
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dealTitle">Deal Title *</Label>
              <Input
                id="dealTitle"
                placeholder="e.g., TechCorp Acquisition"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dealType">Deal Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleInputChange("type", value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select deal type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DEAL_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dealValue">
                {formData.type
                  ? getDealFieldConfig(formData.type as DealType).common.value
                  : "Deal Value"} *
              </Label>
              <CurrencyValueInput
                id="dealValue"
                currencyValue={formData.currency}
                onCurrencyChange={(value: string) =>
                  handleInputChange("currency", value)
                }
                numberValue={formData.value}
                onNumberChange={(value: string) =>
                  handleInputChange("value", value)
                }
                currencyName="currency"
                numberName="dealValue"
                placeholder="e.g., 50000000"
                className="w-full"
                required
              />
            </div>
            <div className="space-y-2">
            <Label htmlFor="jurisdiction">Buyer Jurisdiction *</Label>
            <Popover open={jurisdictionOpen} onOpenChange={setJurisdictionOpen}>
            <PopoverTrigger asChild>
            <Button
             variant="outline"
             role="combobox"
             aria-expanded={jurisdictionOpen}
             className={cn(
             "w-full justify-between font-normal",
             !formData.jurisdiction && "text-muted-foreground",
             validationAttempted && !formData.jurisdiction.trim() && "border-red-300"
          )}
        >
        {formData.jurisdiction || "Select country"}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
       <Command>
        <CommandInput placeholder="Search country..." className="h-9" />
        <CommandList>
          <CommandEmpty>No country found.</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-auto">
            {COUNTRIES.map((country) => (
              <CommandItem
                key={country.code}
                value={country.name}
                onSelect={() => {
                  handleInputChange("jurisdiction", country.name);
                  setJurisdictionOpen(false);
                }}
              >
                {country.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
  {validationAttempted && !formData.jurisdiction.trim() && (
    <p className="text-sm text-red-500">Buyer Jurisdiction is required</p>
  )}
</div>




          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="buyer">
                {formData.type &&
                  getDealFieldConfig(formData.type as DealType).common.buyer.label}
                {!formData.type && "Buyer/Acquirer"} *
              </Label>
              <Input
                id="buyer"
                placeholder={
                  formData.type
                    ? getDealFieldConfig(formData.type as DealType).common.buyer
                        .placeholder
                    : "e.g., Global Holdings Inc"
                }
                value={formData.buyer}
                onChange={(e) => handleInputChange("buyer", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {formData.type
                  ? getDealFieldConfig(formData.type as DealType).common.buyer
                      .description
                  : "The acquiring entity or buyer"}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seller">
                {formData.type &&
                  getDealFieldConfig(formData.type as DealType).common.seller.label}
                {!formData.type && "Seller/Target"} *
              </Label>
              <Input
                id="seller"
                placeholder={
                  formData.type
                    ? getDealFieldConfig(formData.type as DealType).common.seller
                        .placeholder
                    : "e.g., TechCorp Ltd"
                }
                value={formData.seller}
                onChange={(e) => handleInputChange("seller", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {formData.type
                  ? getDealFieldConfig(formData.type as DealType).common.seller
                      .description
                  : "The target company or seller"}
              </p>
            </div>
          </div>

          {/* Deal-Type Specific Fields */}
          {formData.type && (
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-4">
                  {formData.type === "MA" && "M&A Specific Details"}
                  {formData.type === "DEBT_FINANCING" && "Debt Financing Details"}
                  {formData.type === "INVESTMENT" && "Investment Details"}
                </h3>
                <DynamicDealFields
                  dealType={formData.type as DealType}
                  formData={formData}
                  onFieldChange={handleInputChange}
                  onDateChange={handleDateFieldChange}
                  currency={formData.currency}
                />
              </div>
            </div>
          )}

          {/* Suppliers Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Suppliers</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSupplier}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Supplier
              </Button>
            </div>

            {suppliers.map((supplier, index) => (
              <Card key={index} className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Supplier {index + 1}</h4>
                    {suppliers.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSupplier(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`supplier-name-${index}`}>Supplier Name *</Label>
                      <Input
                        id={`supplier-name-${index}`}
                        placeholder="e.g., ABC Corp"
                        value={supplier.name}
                        onChange={(e) =>
                          handleSupplierChange(index, "name", e.target.value)
                        }
                        required
                      />
                    </div>
                <div className="space-y-2">
               <Label htmlFor={`supplier-jurisdiction-${index}`}>
                Seller Jurisdiction *
               </Label>
              <Popover 
             open={supplierJurisdictionOpen[index] || false} 
             onOpenChange={(open) => setSupplierJurisdictionOpen(prev => ({...prev, [index]: open}))}
             >
            <PopoverTrigger asChild>
            <Button
            variant="outline"
            role="combobox"
            aria-expanded={supplierJurisdictionOpen[index] || false}
            className={cn(
          "w-full justify-between font-normal",
          !supplier.jurisdiction && "text-muted-foreground"
           )}
         >
        {supplier.jurisdiction || "Select country"}
       <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
      <Command>
        <CommandInput placeholder="Search country..." className="h-9" />
        <CommandList>
          <CommandEmpty>No country found.</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-auto">
            {COUNTRIES.map((country) => (
              <CommandItem
                key={country.code}
                value={country.name}
                onSelect={() => {
                  handleSupplierChange(index, "jurisdiction", country.name);
                  setSupplierJurisdictionOpen(prev => ({...prev, [index]: false}));
                }}
              >
                {country.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</div>


                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`supplier-contact-${index}`}>Contact Email</Label>
                      <Input
                        id={`supplier-contact-${index}`}
                        type="email"
                        placeholder="e.g., contact@supplier.com"
                        value={supplier.contactInfo}
                        onChange={(e) =>
                          handleSupplierChange(index, "contactInfo", e.target.value)
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Primary contact email for this supplier
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`supplier-description-${index}`}>
                        Description
                      </Label>
                      <Input
                        id={`supplier-description-${index}`}
                        placeholder="Brief description"
                        value={supplier.description}
                        onChange={(e) =>
                          handleSupplierChange(index, "description", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Additional Deal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                placeholder="e.g., Technology, Healthcare"
                value={formData.industry}
                onChange={(e) => handleInputChange("industry", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="riskLevel">Risk Level</Label>
              <Select
                value={formData.riskLevel}
                onValueChange={(value) => handleInputChange("riskLevel", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select risk level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low Risk</SelectItem>
                  <SelectItem value="MEDIUM">Medium Risk</SelectItem>
                  <SelectItem value="HIGH">High Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="confidentialityLevel">Confidentiality Level</Label>
              <Select
                value={formData.confidentialityLevel}
                onValueChange={(value) => handleInputChange("confidentialityLevel", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select confidentiality level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PUBLIC">Public</SelectItem>
                  <SelectItem value="INTERNAL">Internal</SelectItem>
                  <SelectItem value="CONFIDENTIAL">Confidential</SelectItem>
                  <SelectItem value="RESTRICTED">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Brief description of the deal"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional notes about the deal..."
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Additional Parties */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Parties</CardTitle>
          <CardDescription>
            Other parties involved in the deal (advisors, banks, legal counsel, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="additionalParties">Additional Parties</Label>
            <Textarea
              id="additionalParties"
              placeholder="List any additional parties involved (advisors, investment banks, legal counsel, auditors, etc.)"
              rows={3}
            />
            <p className="text-sm text-muted-foreground">
              Optional: Describe other parties and their roles in the transaction
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Key Dates */}
      <Card>
        <CardHeader>
          <CardTitle>Key Dates</CardTitle>
          <CardDescription>Important timeline milestones</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Term Sheet Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !signingDate && "text-muted-foreground",
                      validationAttempted && !signingDate && "border-red-300"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {signingDate ? format(signingDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={signingDate}
                    onSelect={handleSigningDateChange}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {validationAttempted && !signingDate && (
                <p className="text-sm text-red-500">Term Sheet Date is required</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Expected Closing Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !closingDate && "text-muted-foreground",
                      validationAttempted && !closingDate && "border-red-300",
                      validationAttempted &&
                        signingDate &&
                        closingDate &&
                        closingDate <= signingDate &&
                        "border-red-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {closingDate ? format(closingDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={closingDate}
                    onSelect={handleClosingDateChange}
                    initialFocus
                    className="p-3 pointer-events-auto"
                    disabled={(date) => (signingDate ? date <= signingDate : false)}
                  />
                </PopoverContent>
              </Popover>
              {validationAttempted && !closingDate && (
                <p className="text-sm text-red-500">Expected Closing Date is required</p>
              )}
              {validationAttempted &&
                signingDate &&
                closingDate &&
                closingDate <= signingDate && (
                  <p className="text-sm text-red-500">
                    Closing date must be later than term sheet date
                  </p>
                )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes 2 */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
          <CardDescription>
            Any additional information or special considerations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Enter any additional notes, special requirements, or context for this deal..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Link to="/dashboard">
          <Button variant="outline">Cancel</Button>
        </Link>
        <Button type="submit" className="flex items-center gap-2">
          Next: Customize Requisition List
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Step 2 Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Step 2: Customize Requisition List
          </CardTitle>
          <CardDescription>
            Review and customize the document requisition list that will be sent to parties involved in the deal. You can add new items,
            remove existing ones, or modify the requirements as needed.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Loading state */}
      {loadingRequisitions ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Loading requisition template...</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Requisition Categories */}
          <Accordion type="multiple" className="space-y-4">
            {requisitionCategories.map((category, categoryIndex) => {
              const Icon = category.icon;
              return (
                <AccordionItem key={category.id} value={category.id}>
                  <Card>
                    <AccordionTrigger className="hover:no-underline px-4 py-0">
                      <CardHeader className="flex-row items-center space-y-0 space-x-4 w-full">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 text-left">
                          <CardTitle className="text-lg">{category.title}</CardTitle>
                          <CardDescription>{category.items.length} items</CardDescription>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary">{category.items.length} items</Badge>
                        </div>
                      </CardHeader>
                    </AccordionTrigger>
                    <AccordionContent>
                      <CardContent className="pt-0">
                        <div className="space-y-4">
                          {category.items.map((item, itemIndex) => (
                            <div key={item.id || itemIndex} className="border rounded-lg p-4 space-y-2">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-medium text-muted-foreground">
                                      {categoryIndex + 1}.{itemIndex + 1}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium">
                                    {typeof item === "string" ? item : item.name}
                                  </p>
                                  {typeof item === "object" && item.description && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteItemWithConfirmation(category.id, itemIndex)
                                  }
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  title={
                                    dealId
                                      ? "Delete this item and all associated documents"
                                      : "Delete this item"
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}

                          {/* Add new item */}
                          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                            {showNewItemInputs[category.id] ? (
                              <div className="space-y-3">
                                <Textarea
                                  placeholder="Enter new requisition item..."
                                  value={newItemInputs[category.id] || ""}
                                  onChange={(e) =>
                                    setNewItemInputs((prev) => ({
                                      ...prev,
                                      [category.id]: e.target.value,
                                    }))
                                  }
                                  rows={3}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => addNewItem(category.id)}
                                    disabled={!newItemInputs[category.id]?.trim()}
                                  >
                                    Add Item
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      setShowNewItemInputs((prev) => ({
                                        ...prev,
                                        [category.id]: false,
                                      }))
                                    }
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setShowNewItemInputs((prev) => ({
                                    ...prev,
                                    [category.id]: true,
                                  }))
                                }
                                className="w-full flex items-center gap-2 text-muted-foreground hover:text-foreground"
                              >
                                <Plus className="w-4 h-4" />
                                Add New Requisition Item
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </AccordionContent>
                  </Card>
                </AccordionItem>
              );
            })}
          </Accordion>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => navigateToStep(1)} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Deal Details
            </Button>
            <Button onClick={() => navigateToStep(3)} className="flex items-center gap-2">
              Next: Select Personas
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {/* Step 3 Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-blue-500" />
            Step 3: Select Personas
          </CardTitle>
          <CardDescription>
            Choose which AI personas from your settings will analyze this deal. These are the personas you've selected in
            your preferences. They will provide specialized insights based on their expertise areas for this specific deal.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Personas Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Your Selected Personas</CardTitle>
          <CardDescription>
            These are the personas you've configured in your settings. Select which ones should analyze this specific deal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingPersonas ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2 font-light">Loading personas...</span>
            </div>
          ) : personas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Brain className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Personas Selected</h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                You haven't selected any personas in your settings yet. Please go to Settings to configure which AI personas you'd like
                to use for deal analysis.
              </p>
              <Button variant="outline" onClick={() => navigate("/settings")} className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Go to Settings
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {personas.map((persona) => (
                  <div
                    key={persona.type}
                    className="border rounded-lg p-4 space-y-3 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() =>
                      handlePersonaToggle(
                        persona.type,
                        !selectedPersonas.includes(persona.type)
                      )
                    }
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Checkbox
                            id={persona.type}
                            checked={selectedPersonas.includes(persona.type)}
                            onCheckedChange={(checked) =>
                              handlePersonaToggle(persona.type, checked as boolean)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Label htmlFor={persona.type} className="font-medium cursor-pointer">
                            {persona.name}
                          </Label>
                        </div>
                        <p className="text-sm text-muted-foreground font-light mb-2">
                          {persona.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {persona.focusAreas?.slice(0, 3).map((area, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {area}
                            </Badge>
                          ))}
                          {(persona.focusAreas?.length || 0) > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{(persona.focusAreas?.length || 0) - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {selectedPersonas.length} of {personas.length} personas selected
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selectedPersonas.length === personas.length)
                      setSelectedPersonas([]);
                    else setSelectedPersonas(personas.map((p) => p.type));
                  }}
                  className="font-medium"
                >
                  {selectedPersonas.length === personas.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigateToStep(2)} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Requisitions
        </Button>
        {/* move to Step 4 now, not final submit */}
        <Button
          onClick={() => navigateToStep(4)}
          disabled={loading || personas.length === 0}
          className="flex items-center gap-2"
        >
          <ArrowRight className="w-4 h-4" />
          Next: Assign Team
        </Button>
      </div>
    </div>
  );

  // NEW — Step 4 UI
  const renderStep4 = () => (
    <div className="space-y-6">
      {renderAnalystSection()}
      <Card>
        <CardHeader>
          <CardTitle>Assign Team by Persona</CardTitle>
          <CardDescription>
            Pick who will own each persona for this deal. Same user can be assigned to multiple personas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedPersonas?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedPersonas.map((p) => {
                const assignment =
                  personaAssignments[p] ?? ({
                    userId: null,
                    role: "REVIEWER",
                  } as { userId: string | null; role: DealPersonaRole });
                return (
                  <div key={p} className="border rounded-lg p-4 space-y-3">
                    <div className="text-sm font-medium">
                      {p.replace(/_/g, " ")}
                    </div>

                    <div className="space-y-2">
                      <Label>Assignee (Reviewers only)</Label>
<AsyncUserSelect
  role="REVIEWER"
  value={assignment.userId ?? null}
  onChange={(id) => {
    setPersonaAssignments(prev => ({ ...prev, [p]: { ...prev[p], userId: id } }));
  }}
  placeholder="Search reviewers…"
  pageSize={20}
/>
<p className="text-xs text-muted-foreground">
  One reviewer per persona. You can pick the same reviewer for multiple personas.
</p>

                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              No personas selected in Step 3.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Optional Viewers
          </CardTitle>
          <CardDescription>
            These users can view the entire deal without restrictions.
          </CardDescription>
        </CardHeader>
        <CardContent>
         <AsyncUsersMultiSelect
  role="VIEWER"
  value={viewerUserIds}
  onChange={setViewerUserIds}
  placeholder="Search viewers and select…"
  pageSize={20}
/>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigateToStep(3)}>
          Back
        </Button>
        <Button onClick={handleCreateOrUpdateAndAssign} disabled={loading}>
          {loading ? "Saving..." : dealId ? "Update & Assign" : "Create & Assign"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={previousStepNav.previousStepUrl}>
            <Button variant="ghost" size="sm" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {previousStepNav.previousStepText}
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {dealId ? "Edit Deal" : "Register New Deal"} - Step {currentStep} of 4
            </h1>
            <p className="text-muted-foreground">
              {currentStep === 1
                ? dealId
                  ? "Edit deal details"
                  : "Create a new due diligence process"
                : currentStep === 2
                ? "Customize document requirements"
                : currentStep === 3
                ? "Select personas"
                : "Assign team"}
            </p>
          </div>
        </div>

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Requisition Item</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && (
                <>
                  Are you sure you want to delete "<strong>{itemToDelete.itemName}</strong>"?
                  {itemToDelete.hasDocuments && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-amber-800 text-sm">
                        <strong>Warning:</strong> This will also permanently delete any associated documents that have been uploaded for this item.
                      </p>
                    </div>
                  )}
                  <p className="mt-2 text-sm">This action cannot be undone.</p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RegisterDeal;
